import { useNavigate } from 'react-router-dom'
import { Lock, TimelineEmptyIllustration } from '@/assets/icons'
import { matterPaths } from '@/routing/paths'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import MatterRecordEmptyState from '@/matter-asset-common/asset/MatterRecordEmptyState'
import { selectMatterById } from '@/matter/store/selectors'
import { canEditTimeline } from '@/matter/utils/matter-permissions'
import { selectAddFilesStagingCountsForMatter, selectInitializeError } from '../store/selectors'
import { TimelineAssetActions } from '../store/slice'
import { kTimelineOwnerOnlyBuildMessage, kTimelineOwnerOnlyBuildOwnerMessage } from '../constants'
import MatterRecordReadOnlyNotice from '@/matter-asset-common/asset/MatterRecordReadOnlyNotice'
import { useMatterOwnerName } from '@/matter/hooks/useMatterOwnerName'

interface TimelineAssetEmptyStateProps {
  matterId: string
}

/**
 * Blank state shown when the matter has no timeline yet. Thin wrapper over the shared
 * MatterRecordEmptyState that supplies the timeline copy, illustration, CTA, and the
 * first-generation-error banner (shown after a failed Generate).
 */
export default function TimelineAssetEmptyState(props: TimelineAssetEmptyStateProps) {
  const { matterId } = props
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const initializeError = useAppSelector((state: RootState) => selectInitializeError(state, { matterId }))
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId }))
  // Read-only collaborators can't initiate a build — keep the CTA visible but disabled with an explanatory tooltip.
  // Gate on a loaded matter so the disabled state doesn't flash for owners during load.
  const isReadOnly = !!matter && !canEditTimeline(matter)
  const ownerName = useMatterOwnerName(matterId)
  const stagingCounts = useAppSelector((state: RootState) => selectAddFilesStagingCountsForMatter(state, { matterId }))
  const stagingTotal = stagingCounts.pendingCount + stagingCounts.stagedCount

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Owner-attribution notice: only shown here in the pre-build empty state. Once a timeline is initiated (in flight / built), the read-only treatment moves to the tabs-row "Viewing" pill. */}
      {isReadOnly && (
        <div className="pt-4 px-4">
          <MatterRecordReadOnlyNotice
            icon={<Lock />}
            headline={ownerName ? `${ownerName} is the owner of this matter.` : undefined}
            message={ownerName ? kTimelineOwnerOnlyBuildOwnerMessage : kTimelineOwnerOnlyBuildMessage}
          />
        </div>
      )}
      <MatterRecordEmptyState
        illustration={<TimelineEmptyIllustration className="h-10 w-[63px] shrink-0" />}
        title="Build your timeline"
        body="Build a complete timeline of the key events from your case files, with every event cited to its source."
        ctaLabel="Add Files"
        onCtaClick={() => navigate(matterPaths.timeline.addFiles(matterId))}
        ctaDisabled={isReadOnly}
        ctaTooltip={isReadOnly ? kTimelineOwnerOnlyBuildMessage : undefined}
        notice={
          stagingTotal > 0
            ? `${stagingTotal} ${stagingTotal === 1 ? 'file' : 'files'} ${stagingCounts.pendingCount > 0 ? 'uploading' : 'ready'} — continue in Add Files`
            : undefined
        }
        error={
          initializeError
            ? {
                headline: 'Timeline could not be generated.',
                detail: 'Please try again.',
                dismissLabel: 'Dismiss',
                onDismiss: () => dispatch(TimelineAssetActions.setInitializeError({ matterId, error: null })),
              }
            : undefined
        }
      />
    </div>
  )
}
